/**
 * Custom hook for fetching and managing authenticated video
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { resolvePlayableVideo } from '@/utils/playableVideo'

interface UseAuthenticatedVideoReturn {
  videoUrl: string | null
  isLoading: boolean
  error: string | null
  loadVideo: (url: string, token: string) => Promise<void>
  clearVideo: () => void
}

export function useAuthenticatedVideo(): UseAuthenticatedVideoReturn {
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  const clearVideo = useCallback(() => {
    if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    setVideoUrl(null)
    setError(null)
  }, [])

  const loadVideo = useCallback(async (url: string, token: string) => {
    try {
      setIsLoading(true)
      setError(null)
      clearVideo()

      // Presigned S3 URL (or a blob: URL for local storage). clearVideo only
      // revokes blob: URLs, so tracking a plain presigned URL here is harmless.
      const resolvedUrl = await resolvePlayableVideo(url, token)
      blobUrlRef.current = resolvedUrl
      setVideoUrl(resolvedUrl)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load video'
      setError(errorMessage)
      setVideoUrl(null)
    } finally {
      setIsLoading(false)
    }
  }, [clearVideo])

  useEffect(() => {
    return () => {
      clearVideo()
    }
  }, [clearVideo])

  return {
    videoUrl,
    isLoading,
    error,
    loadVideo,
    clearVideo,
  }
}







